import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import './BetaCountdown.css';


// Cache für API-Aufrufe (2 Minuten)
const cache = new Map();
const CACHE_DURATION = 2 * 60 * 1000; // 2 Minuten

const cachedFetch = async (url, cacheKey) => {
  const now = Date.now();
  const cached = cache.get(cacheKey);
  
  if (cached && (now - cached.timestamp < CACHE_DURATION)) {
    return cached.data;
  }
  
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    cache.set(cacheKey, { data, timestamp: now });
    return data;
  } catch (error) {
    if (cached) {
      console.warn(`Using expired cache for ${cacheKey}:`, error);
      return cached.data;
    }
    throw error;
  }
};

function BetaCountdown() {
  const [countdownData, setCountdownData] = useState(null);
  const [featuresData, setFeaturesData] = useState({ current: [], future: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [isMobile, setIsMobile] = useState(false);
  const [isCurrentTabOpen, setIsCurrentTabOpen] = useState(true); // Desktop: current offen

  // Animierte Zahlen State
  const [animatedCurrent, setAnimatedCurrent] = useState(0);
  const [animatedTarget, setAnimatedTarget] = useState(2500);

  // Screen Size Detection
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Desktop: current tab standardmäßig offen
      if (!mobile && isCurrentTabOpen === null) {
        setIsCurrentTabOpen(true);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [isCurrentTabOpen]);

  // Memoized Progress Calculation
  const progressData = useMemo(() => {
    if (!countdownData) return { percent: 0, remaining: 2500 };
    
    const percent = Math.min(Math.round((animatedCurrent / animatedTarget) * 100), 100);
    const remaining = Math.max(animatedTarget - animatedCurrent, 0);
    
    return { percent, remaining };
  }, [animatedCurrent, animatedTarget, countdownData]);

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Parallele API-Aufrufe
      const [countdownResponse, featuresResponse] = await Promise.all([
        cachedFetch(`${API_URL}/api/beta-countdown`, 'beta-countdown'),
        cachedFetch(`${API_URL}/api/beta-features`, 'beta-features')
      ]);

      // Countdown Data
      if (countdownResponse.success) {
        setCountdownData(countdownResponse.data);
        
        const newCurrent = countdownResponse.data.currentMembers;
        const newTarget = countdownResponse.data.targetMembers;
        
        animateNumber(animatedCurrent, newCurrent, setAnimatedCurrent, 1000);
        animateNumber(animatedTarget, newTarget, setAnimatedTarget, 800);
      }

      // Features Data
      if (featuresResponse.success) {
        setFeaturesData(featuresResponse.data);
        
        // Initialize default features if none exist
        if (featuresResponse.data.current.length === 0 && featuresResponse.data.future.length === 0) {
          await fetch(`${API_URL}/api/beta-features/initialize`, { method: 'POST' });
          // Reload features after initialization
          const newFeaturesResponse = await cachedFetch(`${API_URL}/api/beta-features`, 'beta-features-reload');
          if (newFeaturesResponse.success) {
            setFeaturesData(newFeaturesResponse.data);
          }
        }
      }

    } catch (error) {
      console.error('Beta Section Error:', error);
      setError('Failed to load beta section data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Number animation function
  const animateNumber = (start, end, setter, duration = 1000) => {
    if (start === end) return;
    
    const startTime = Date.now();
    const difference = end - start;
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(start + (difference * easeOut));
      
      setter(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // Tab click handler
  const handleTabClick = (tab) => {
    if (isMobile) {
      // Mobile: Toggle behavior
      if (tab === 'current') {
        setIsCurrentTabOpen(!isCurrentTabOpen);
      } else {
        setActiveTab(tab);
        setIsCurrentTabOpen(false);
      }
    } else {
      // Desktop: Normal tab switching
      setActiveTab(tab);
    }
  };

  // Loading state
  if (loading) {
    return (
      <section className="beta-section">
        <div className="beta-section-container">
          <div className="beta-section-loading">
            <div className="loading-spinner"></div>
            <p>Loading beta information...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="beta-section">
        <div className="beta-section-container">
        <div className="beta-section-error">
          <h3>Loading Error</h3>
            <p>{error}</p>
            <button 
              className="va-btn primary" 
              onClick={() => {
                cache.clear();
                loadData();
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Don't show if countdown not active
  if (!countdownData || !countdownData.isActive) {
    return null;
  }

  return (
    <section className="beta-section">
      <div className="beta-section-container">
        {/* Header with Countdown */}
        <div className="beta-header">
          <div className="beta-header-content">
            <h2 className="beta-title">{countdownData.title}</h2>
            <p className="beta-subtitle">{countdownData.subtitle}</p>
            
            {/* Compact Countdown */}
            <div className="beta-countdown-compact">
              <div className="countdown-stats">
                <span className="stat-number">{animatedCurrent.toLocaleString()}</span>
                <span className="stat-divider">of</span>
                <span className="stat-number">{animatedTarget.toLocaleString()}</span>
                <span className="stat-label">beta members</span>
              </div>
              
              <div className="countdown-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progressData.percent}%` }}
                  ></div>
                </div>
                <span className="progress-text">{progressData.percent}% complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features Tabs */}
        <div className="beta-features">
          <div className="features-tabs">
            <button
              className={`tab-button ${(isMobile && isCurrentTabOpen) || (!isMobile && activeTab === 'current') ? 'active' : ''}`}
              onClick={() => handleTabClick('current')}
            >
              <span className="tab-text">Current Features</span>
              <span className="tab-count">({featuresData.current.length})</span>
              {isMobile && (
                <span className="tab-chevron">{isCurrentTabOpen ? '▼' : '▶'}</span>
              )}
            </button>
            
            <button
              className={`tab-button ${(!isMobile && activeTab === 'future') ? 'active' : ''}`}
              onClick={() => handleTabClick('future')}
            >
              <span className="tab-text">Future Features</span>
              <span className="tab-count">({featuresData.future.length})</span>
              {isMobile && (
                <span className="tab-chevron">{activeTab === 'future' ? '▼' : '▶'}</span>
              )}
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Current Features */}
            {((isMobile && isCurrentTabOpen) || (!isMobile && activeTab === 'current')) && (
              <div className="features-grid">
                {featuresData.current.map((feature, index) => (
                  <div key={feature._id || index} className="feature-card">
                    <div className="feature-content">
                      <h4 className="feature-title">{feature.title}</h4>
                      <p className="feature-description">{feature.description}</p>
                      {feature.status === 'completed' && (
                        <span className="feature-badge completed">Live</span>
                      )}
                      {feature.status === 'active' && (
                        <span className="feature-badge active">Active</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Future Features */}
            {((!isMobile && activeTab === 'future') || (isMobile && activeTab === 'future')) && (
              <div className="features-grid">
                {featuresData.future.map((feature, index) => (
                  <div key={feature._id || index} className="feature-card future">
                    <div className="feature-content">
                      <h4 className="feature-title">{feature.title}</h4>
                      <p className="feature-description">{feature.description}</p>
                      <div className="feature-meta">
                        <span className="feature-badge future">Coming Soon</span>
                        {feature.estimatedRelease && feature.estimatedRelease !== 'TBA' && (
                          <span className="feature-release">{feature.estimatedRelease}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default BetaCountdown;